import React, { ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import classNames from 'classnames';

import { Spinner } from '@uikit';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

interface InfiniteQueryStateProps {
  isEmpty: boolean;
  query: UseInfiniteQueryResult<InfiniteData<any, unknown>, unknown>;
  loading?: ReactNode;
  className?: string;
  triggerClassName?: string;
  empty?: ReactNode;
}

const InfiniteQueryState = ({
  query,
  triggerClassName,
  empty,
  isEmpty,
  loading,
  className,
}: InfiniteQueryStateProps) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    error,
    refetch,
    hasNextPage,
    isError,
  } = query;
  const { ref } = useInView({
    onChange: (inView) =>
      inView && !isError && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const showLoader = !isError && (isLoading || hasNextPage);
  const showEmpty = !isLoading && !isError && isEmpty;
  const showError = !isLoading && isError;

  const handleTryAgainButtonClick = () => {
    if (isEmpty) {
      refetch();
    } else {
      fetchNextPage();
    }
  };

  if (!showLoader && !showEmpty && !showEmpty) {
    return null;
  }

  return (
    <div className={classNames('flex flex-col flex-1', className)}>
      {showLoader && (
        // Key is added to trigger in view cb again if component is already in view
        <div ref={ref} key={data?.pages.length} className={triggerClassName}>
          {typeof loading === 'undefined' ? <Spinner className="m-auto" /> : loading}
        </div>
      )}

      {showEmpty && (empty || <p className="m-auto text-center text-[#9A9AAC]">Empty</p>)}

      {showError && (
        <div className="flex flex-col gap-3 m-auto">
          <p className="text-center text-secondary-600">
            Error happened while loading:
            <br />
            {getErrorMessage(error)}
          </p>
          <button
            className="msq-btn msq-btn-md msq-btn-primary mx-auto"
            onClick={handleTryAgainButtonClick}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default InfiniteQueryState;
